import React, { useState } from "react";
import { TRACKING_EVENT_TYPE } from "app-shell/GoogleTagManager/constants";
import { pushToDataLayer } from "app-shell/GoogleTagManager/GoogleTagManager";
import { BillingContextProvider } from "component-library";
import {
  CheckoutAccessTypes,
  CheckoutFlow,
  PlanCodePeriod,
  IPlanPeriodData,
  PlanFilter,
} from "component-library/src/components/Organisms/Billing/types";
import { SHOW_SUBSCRIPTION_CHANGE_SUCCESS_PARAM } from "config/common";

import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import {
  changeSubscription,
  getRecurlyDetails,
  IBillingApiResponse,
  MY_SUBSCRIPTION_QK,
} from "services/api/BillingApi/BillingApi";
import applicationMap from "services/applicationMap";
import useAddOnCheckoutSteps from "../hooks/useAddOnCheckoutPages";
import { useGetBillingContextDefaultProps } from "../hooks/useGetBillingContextDefaultProps";
import { formatDateToDayMonthYear } from "../helpers/helpers";
import { useTranslation } from "i18n";
import { HeapCustomEvent, HeapService } from "shared-services";

export default function ChangeWrapper() {
  const history = useHistory();
  const { t } = useTranslation();

  const { data: mySubscriptionData } = useQuery<
    IBillingApiResponse | undefined
  >({
    queryKey: MY_SUBSCRIPTION_QK,
    queryFn: async () => await getRecurlyDetails(),
    cacheTime: 0,
  });
  const [isHeapServiceTrackCalled, setIsHeapServiceTrackCalled] =
    useState(false);

  const defaultProps = useGetBillingContextDefaultProps(
    CheckoutAccessTypes.RenewalReactivation
  );
  const { Component, setAddOnCheckoutStep, addOnCheckoutStep } =
    useAddOnCheckoutSteps();

  const handleGoBack = () => {
    HeapService.track(HeapCustomEvent.PerformerChangePlanCancelled);

    if (addOnCheckoutStep) return setAddOnCheckoutStep(0);

    history.goBack();
  };

  const handleGoNext = () => {
    if (!addOnCheckoutStep) setAddOnCheckoutStep(1);
  };

  const postCheckoutCallback = async (
    data: any,
    trackingData: Record<string, unknown>
  ) => {
    await changeSubscription({ planId: data.planId });

    pushToDataLayer({
      event: TRACKING_EVENT_TYPE.ChangeSubscription,
      memberTier: data?.planTypeFilter,
      ...trackingData,
    });
  };

  const { subscription } = mySubscriptionData as IBillingApiResponse;

  const planEndDate = formatDateToDayMonthYear(subscription.currentTermEndsAt);

  const customTexts = {
    subscriptionStepPageTitle:
      "common:billing.subscriptionStep.planChange.pageTitle",
    subscriptionStepPageDescr: "billing.subscriptionStep.planChange.pageDescr",
    subscriptionStepPageDescrStrong:
      "billing.subscriptionStep.planChange.pageDescrStrong",
    checkoutStepPageDescr: "billing.subscriptionStep.planChange.pageDescr",
    planEndDateTitle: t(
      "common:billing.subscriptionStep.planChange.pageTitleWithDate",
      { planEndDate }
    ),
    planEndDateMessage: t(
      "common:billing.checkout.orderSummary.subscriptionChanges",
      {
        planEndDate,
      }
    ),
  };

  return (
    <BillingContextProvider
      {...defaultProps}
      checkoutFlow={CheckoutFlow.ChangePlan}
      goBack={handleGoBack}
      goNext={handleGoNext}
      postCheckoutCallback={postCheckoutCallback}
      enableAddOnsSelection={false}
      navigateToCompletePage={() =>
        history.push(
          `${applicationMap.routes.settingsMySubscription()}?${SHOW_SUBSCRIPTION_CHANGE_SUCCESS_PARAM}=1`
        )
      }
      customTexts={customTexts}
      selectPerionCallback={(
        plan: PlanCodePeriod,
        planPeriodData: IPlanPeriodData,
        preselectedPlanOverride: PlanCodePeriod | PlanFilter | undefined
      ) => {
        if (
          plan !== planPeriodData.selectedPeriod &&
          plan !== preselectedPlanOverride &&
          !isHeapServiceTrackCalled
        ) {
          setIsHeapServiceTrackCalled(true);
          HeapService.track(HeapCustomEvent.PerformerAnotherPlan);
        }
      }}
    >
      <Component />
    </BillingContextProvider>
  );
}
